import {
  collection,
  type DocumentData,
  deleteField,
  doc,
  getDocs,
  type QuerySnapshot,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { Draft } from '../components/TenderDetails/types';
import { getLoadingKey } from '../components/TenderDetails/utils';
import { regenerateDocument } from '../services/api';
import { firestore } from '../services/firebase';
import type { User } from '../types/auth';
import type { Tender } from '../types/tender';
import { logDocumentActivity } from '../utils/activityLogger';

const EMPTY_OBJECT: Record<string, never> = {};

export interface UseTenderGeneratorsProps {
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  setTender: React.Dispatch<React.SetStateAction<Tender | null>>;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  drafts: Draft[];
}

export const useTenderGenerators = ({
  tender,
  account,
  setTender,
  showSnackbar,
  setLoadingStates,
  drafts,
}: UseTenderGeneratorsProps) => {
  const handleRegenerate = async (docType: string, reviewSource?: 'upload' | 'sections') => {
    if (!account?.uid || !account.clientId || !tender?.id) {
      showSnackbar('Cannot regenerate: Missing critical user, client, or tender details.', 'error');
      return;
    }

    const loadingKey = getLoadingKey(
      tender.id,
      'regenerate',
      docType === 'response'
        ? 'draft_generation'
        : docType === 'review' && reviewSource === 'sections'
          ? 'quick_review'
          : docType
    );
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      if (!account.actualDefaultBucketName) {
        throw new Error('User account is not fully configured for document generation.');
      }

      // Capture sections to delete BEFORE calling API (to avoid race condition with new sections)
      let oldSectionsSnapshot: QuerySnapshot<DocumentData> | null = null;
      if (docType === 'response') {
        try {
          const sectionsRef = collection(firestore, 'tenders', tender.id, 'sections');
          oldSectionsSnapshot = await getDocs(sectionsRef);
        } catch (_err) {
          // Best effort
        }
      }

      const payload = {
        clientId: account.clientId,
        userId: account.uid,
        tenderId: tender.id,
        userSpecificBucket: account.actualDefaultBucketName,
        docType: docType,
        sourceDraftStorageUri: undefined as string | undefined,
        sourceDraftGcsPath: undefined as string | undefined,
        sourceDraftFileName: undefined as string | undefined,
        reviewSource: reviewSource,
      };

      if (docType === 'review' && reviewSource !== 'sections') {
        if (!drafts || drafts.length === 0) {
          showSnackbar('No drafts available to regenerate a review document.', 'warning');
          return;
        }
        const latestDraft = drafts[0];
        if (latestDraft?.conversionStatus !== 'success') {
          showSnackbar('The latest draft has not been successfully converted yet.', 'warning');
          return;
        }
        const draftFileName = latestDraft.filename || latestDraft.name;
        if (!latestDraft || (!latestDraft.storageUri && !latestDraft.gcsPath) || !draftFileName) {
          showSnackbar('Latest draft is missing storage URI or name.', 'error');
          return;
        }
        payload.sourceDraftStorageUri = latestDraft.storageUri || latestDraft.gcsPath;
        payload.sourceDraftGcsPath = latestDraft.gcsPath;
        payload.sourceDraftFileName = draftFileName;
      }

      // Call API first (Limit Check happens here)
      const response = await regenerateDocument({
        ...payload,
        sourceDraftStorageUri: payload.sourceDraftStorageUri || '',
        sourceDraftGcsPath: payload.sourceDraftGcsPath || '',
        sourceDraftFileName: payload.sourceDraftFileName || '',
      });
      const successResponse = response as { success?: boolean; message?: string };

      if (successResponse?.success) {
        // --- Success! Now we can clear data and update status ---

        // 1. Update Firestore Status & Clear Data
        try {
          const tenderRef = doc(firestore, 'tenders', tender.id);
          const updates: Record<string, unknown> = {};

          if (docType === 'alignmentAnalysis') {
            updates['alignmentAnalysis.status'] = 'Generating...';
            updates['alignmentAnalysis.progress'] = 0;
          } else if (docType === 'response') {
            updates['draftResponse.status'] = 'Generating...';
            updates['draftResponse.progress'] = 0;
            // Clear the download buttons
            updates.draftFileLink = deleteField();
            updates.compiledFileLink = deleteField();

            // Clear sections subcollection (using captured snapshot)
            if (oldSectionsSnapshot && !oldSectionsSnapshot.empty) {
              const batch = writeBatch(firestore);
              for (const doc of oldSectionsSnapshot.docs) {
                batch.delete(doc.ref);
              }
              await batch.commit();
            }
          } else if (docType === 'review') {
            updates['reviewResponse.status'] = 'Generating...';
            updates['reviewResponse.lastType'] = reviewSource === 'sections' ? 'quick' : 'final';
          }
          await updateDoc(tenderRef, updates);
        } catch (_e) {
          // Ignore
        }

        // 2. Update Local State Immediately
        const statusField =
          docType === 'alignmentAnalysis'
            ? 'alignmentAnalysis'
            : docType === 'response'
              ? 'draftResponse'
              : 'reviewResponse';

        if (tender) {
          setTender((prev: Tender | null) => {
            if (!prev) {
              return prev;
            }
            const updated = { ...prev };

            // Update status
            if (statusField === 'alignmentAnalysis') {
              updated.alignmentAnalysis = {
                ...(updated.alignmentAnalysis || EMPTY_OBJECT),
                status: 'processing',
              };
            } else if (statusField === 'draftResponse') {
              updated.draftResponse = {
                ...(updated.draftResponse || EMPTY_OBJECT),
                status: 'processing',
              };
              // Clear links locally
              delete updated.draftFileLink;
              delete updated.compiledFileLink;
            } else if (statusField === 'reviewResponse') {
              updated.reviewResponse = {
                ...(updated.reviewResponse || EMPTY_OBJECT),
                status: 'processing',
                lastType: reviewSource === 'sections' ? 'quick' : 'final',
              };
            }
            return updated;
          });
        }

        showSnackbar(`Regenerating your document, this may take a few minutes.`, 'success');
        logDocumentActivity.regenerated({
          type: docType,
          fileName: docType,
          source: `tender_details_dialog/${tender.id}`,
        });
        if (docType === 'review') {
          logDocumentActivity.reviewed({
            type: docType,
            fileName: docType,
            source: `tender_details_dialog/${tender.id}`,
          });
        }
      } else {
        throw new Error(successResponse?.message || 'Failed to start regeneration.');
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { code?: string; message?: string } };
        message?: string;
      };
      // The API interceptor no longer shows a snackbar for 429 errors, so we handle it here.
      if (err.response?.status === 429 && err.response?.data?.code === 'limit-exceeded') {
        showSnackbar(`⚠️ ${err.response.data.message}`, 'warning', 7000);
      } else if (
        (err.response?.status === 403 || err.response?.status === 400) &&
        err.response?.data?.message
      ) {
        // Check for limit/subscription errors in response body
        const msg = err.response.data.message.toLowerCase();
        if (
          msg.includes('limit') ||
          msg.includes('billing period') ||
          msg.includes('subscription') ||
          msg.includes('renew')
        ) {
          // Handled by global interceptor or specific logic - suppress generic error
        } else {
          showSnackbar(
            `Failed to regenerate ${docType}: ${err.response.data.message}`,
            'error',
            7000
          );
        }
      } else if (
        err.message &&
        (err.message.includes('monthly limit') || err.message.includes('for this tender'))
      ) {
        // Client-side pre-check
        showSnackbar(`⚠️ LIMIT REACHED: ${err.message}`, 'warning', 7000);
      } else {
        // Handle all other errors
        const isServerError = err.response?.status && err.response.status >= 500;
        const serverMsg = isServerError
          ? 'An internal server error occurred.'
          : err.response?.data?.message || err.message;

        showSnackbar(`Failed to regenerate ${docType}: ${serverMsg}`, 'error', 7000);
      }
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleQuickReview = async () => {
    await handleRegenerate('review', 'sections');
  };

  return {
    handleRegenerate,
    handleQuickReview,
  };
};
